import { ref, type Ref } from "vue";

import { useTranslate } from "@common/composables/useTranslate";

interface ErrorModalState {
  isOpen: Ref<boolean>;
  title: Ref<string>;
  message: Ref<string>;
  variant: Ref<"error" | "success">;
  actionLabel: Ref<string | undefined>;
  confirmHandler: (() => void) | null;
}

const createErrorModalState = (): ErrorModalState => ({
  isOpen: ref(false),
  title: ref(""),
  message: ref(""),
  variant: ref<"error" | "success">("error"),
  actionLabel: ref<string | undefined>(undefined),
  confirmHandler: null
});

let sharedErrorModalState: ErrorModalState | null = null;

const getErrorModalState = () => {
  sharedErrorModalState ??= createErrorModalState();
  return sharedErrorModalState;
};

interface ModalOptions {
  actionLabel?: string;
  onConfirm?: () => void;
}

interface ErrorModalController {
  isOpen: Ref<boolean>;
  title: Ref<string>;
  message: Ref<string>;
  variant: Ref<"error" | "success">;
  actionLabel: Ref<string | undefined>;
  showError: (msg: string, t?: string, options?: ModalOptions) => void;
  showSuccess: (msg: string, t?: string, options?: ModalOptions) => void;
  close: () => void;
  confirm: () => void;
}

export function useErrorModal(): ErrorModalController {
  const translate = useTranslate();
  const state = getErrorModalState();

  const applyOptions = (options?: ModalOptions) => {
    state.actionLabel.value = options?.actionLabel;
    state.confirmHandler = options?.onConfirm ?? null;
  };

  const showError = (msg: string, t?: string, options?: ModalOptions) => {
    state.message.value = msg ?? "";
    state.title.value = t ?? translate("common.errorTitle");
    state.variant.value = "error";
    applyOptions(options);
    state.isOpen.value = true;
  };

  const showSuccess = (msg: string, t?: string, options?: ModalOptions) => {
    state.message.value = msg ?? "";
    state.title.value = t ?? translate("common.successTitle");
    state.variant.value = "success";
    applyOptions(options);
    state.isOpen.value = true;
  };

  const close = () => {
    state.isOpen.value = false;
    state.actionLabel.value = undefined;
    state.confirmHandler = null;
  };

  const confirm = () => {
    const handler = state.confirmHandler;
    close();
    if (handler) {
      handler();
    }
  };

  return {
    isOpen: state.isOpen,
    title: state.title,
    message: state.message,
    variant: state.variant,
    actionLabel: state.actionLabel,
    showError,
    showSuccess,
    close,
    confirm
  };
}
